import _ from 'lodash';
import React, { useCallback } from 'react';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import RoomItem from 'components/room-map/room-item';

import { useOpenBookingDetail } from 'hooks/useOpenBookingDetail';

import {
  DISPLAY_ROOM_VALUE,
  FILTER_DISPLAY_ROOM_MAP_VALUE,
  FILTER_STATUS_ROOM_MAP_VALUE,
  MAP_FILTER_STATUS_LABEL
} from 'constants/common';

import { toLocalTime } from 'utils';
import { GroupedRoomStatus } from 'services/api/type/room.type';

const BRANCH_IDS_HARD_FLOOR = [9, 10]; // TODO: handle hard branch ids to get the room following floor mapping

interface Props {
  rooms: GroupedRoomStatus[] | undefined;
  filterDisplay: FILTER_DISPLAY_ROOM_MAP_VALUE;
  displayRoom: DISPLAY_ROOM_VALUE;
  status: FILTER_STATUS_ROOM_MAP_VALUE;
  searchHook: string;
}

function RoomMapSection({ rooms, filterDisplay, displayRoom, status, searchHook }: Props) {
  const { t } = useTranslation();

  const { openBookingDetail } = useOpenBookingDetail();

  const isTight =
    filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR &&
    displayRoom === DISPLAY_ROOM_VALUE.SIMPLE;

  // use callback to get room by status or is clean
  const getRoomsByStatus = useCallback(
    (status: FILTER_STATUS_ROOM_MAP_VALUE) => {
      if (!rooms) {
        return [];
      }
      if (status === FILTER_STATUS_ROOM_MAP_VALUE.ALL) {
        const sortedData = rooms
          .sort((a, b) => a.attributes?.room_no?.localeCompare(b.attributes?.room_no))
          .filter(item =>
            item.attributes?.room_no?.includes(searchHook.toLocaleLowerCase().trim())
          );
        return sortedData;
      }
      return rooms
        .filter(item => {
          return (
            (item.room_status === status ||
              (!item.is_clean && status === FILTER_STATUS_ROOM_MAP_VALUE.DIRTY)) &&
            item.attributes?.room_no?.includes(searchHook.toLocaleLowerCase().trim())
          );
        })
        .sort((a, b) => a.attributes?.room_no?.localeCompare(b.attributes?.room_no));
    },
    [rooms, searchHook]
  );

  const handleViewDetail = (bookingLineId: number) => {
    openBookingDetail(bookingLineId);
  };

  const groupDataByBookingStatus = () => {
    const groupedData = _.groupBy(getRoomsByStatus(status), item => item.room_status);
    const newOrderedGroup: _.Dictionary<GroupedRoomStatus[]> = {};
    [
      FILTER_STATUS_ROOM_MAP_VALUE.NOT_CHECKOUT,
      FILTER_STATUS_ROOM_MAP_VALUE.CHECKIN,
      FILTER_STATUS_ROOM_MAP_VALUE.NO_SHOW,
      FILTER_STATUS_ROOM_MAP_VALUE.BOOKED,
      FILTER_STATUS_ROOM_MAP_VALUE.AVAILABLE,
      FILTER_STATUS_ROOM_MAP_VALUE.LOCKED
    ].forEach(key => {
      if (key in groupedData) {
        _.set(newOrderedGroup, key, groupedData[key]);
      }
    });

    return newOrderedGroup;
  };

  const groupDataByCategories = () => {
    const groupedData = _.groupBy(getRoomsByStatus(status), item => item.room_type_name);
    return groupedData;
  };

  const groupDataByFloor = () => {
    const groupedData = _.groupBy(getRoomsByStatus(status), item => {
      const roomNo = item.attributes?.room_no || '';
      const MIN_FLOOR = 1;
      const MAX_FLOOR = 19;
      const FLOOR_OFFSET = 10;
      const len = roomNo.length;

      if (BRANCH_IDS_HARD_FLOOR.includes(item.branch_id) && len === 4) {
        const floorNum = Number(roomNo.slice(0, 2));
        return floorNum >= MIN_FLOOR && floorNum <= MAX_FLOOR
          ? String(floorNum - FLOOR_OFFSET)
          : roomNo.slice(0, 2);
      }

      if (len === 3) return roomNo[0];
      if (len === 4) return roomNo.slice(0, 2);

      return roomNo;
    });

    // if (isTight) {
    //   Object.keys(groupedData).forEach(key => {
    //     const arr = groupedData[key];
    //     console.log('11111111', arr, key);
    //     arr.unshift({
    //       label: (
    //         <p className="m-0">
    //           {t('bookingPage.roomMap.floor')} <br /> {key}
    //         </p>
    //       ),
    //       isLabel: true
    //     } as any);
    //   });
    // }

    return groupedData;
  };

  const groupDataByGroup = () => {
    const statusFilteredRooms = getRoomsByStatus(status);
    const groupFilteredRooms = statusFilteredRooms.filter(
      item => !!item.booking_line?.is_group || !!item.group
    );

    const groupedData = _.groupBy(groupFilteredRooms, item => {
      if (item?.booking_line?.group_master_id) {
        return `[${item.booking_line.group_master_id}] [${item.booking_line.group_master_name}] (Group)`;
      } else {
        return `[${item.booking_line?.moved_booking_id}] [${item.booking_line?.group_name}] (Party)`;
      }
    });
    return groupedData;
  };

  const groupDataBySection = (data?: GroupedRoomStatus[]) => {
    const groupedData = _.groupBy(data || getRoomsByStatus(status), item => item.section);
    return groupedData;
  };

  const getLabelByFloor = (floor: string) => {
    return Number(floor);
  };

  const renderByFilterDisplay = () => {
    if (
      [
        FILTER_DISPLAY_ROOM_MAP_VALUE.BOOKING,
        FILTER_DISPLAY_ROOM_MAP_VALUE.CATEGORIES,
        FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR,
        FILTER_DISPLAY_ROOM_MAP_VALUE.GROUP
      ].includes(filterDisplay)
    ) {
      let groupedData: any = {};

      if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.BOOKING) {
        groupedData = groupDataByBookingStatus();
      } else if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.CATEGORIES) {
        groupedData = groupDataByCategories();
      } else if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR) {
        groupedData = groupDataByFloor();
      } else if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.GROUP) {
        groupedData = groupDataByGroup();
      }

      const arrayKeys = Object.keys(groupedData);
      // if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR) {
      //   const maxFloor = getMaxKeyFloor(groupedFullData);
      //   arrayKeys = Array.from({ length: maxFloor }, (_, i) => String(i + 1));
      // }
      // if (filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.BOOKING) {
      //   arrayKeys = getMaxKeyBooking(groupedData);
      // }

      const groupedDataSection = groupDataBySection();
      const fullArrayKeysBySection = Object.keys(groupedDataSection);
      fullArrayKeysBySection.sort((a, b) => a.localeCompare(b));

      if (isTight) {
        // const isGroupDisplay =
        //   (filterDisplay as FILTER_DISPLAY_ROOM_MAP_VALUE) === FILTER_DISPLAY_ROOM_MAP_VALUE.GROUP;

        return (
          <Row gutter={[20, 24]} className="w-full">
            {arrayKeys.map((key, index) => {
              const splitBySection = groupDataBySection(groupedData[key]);

              return (
                <Col span={24} key={index}>
                  <Row gutter={[20, 24]} className="w-full">
                    {fullArrayKeysBySection.map((keySplit: any, indexSplit: number) => {
                      const section = splitBySection[keySplit];

                      return (
                        <Col span={24} lg={12} key={indexSplit}>
                          <div className="pms-room-map__rooms-section">
                            <div className="pms-room-map__rooms-section__wrapper box">
                              {isTight &&
                                DISPLAY_ROOM_VALUE.SIMPLE &&
                                FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR &&
                                !!section?.length && (
                                  <div className={classNames('pms-room-item pms-room-item--floor')}>
                                    <p className="m-0">
                                      {t('bookingPage.roomMap.floor')} <br /> {key}
                                    </p>
                                  </div>
                                )}

                              {section?.map((item: GroupedRoomStatus, itemIndex: number) => {
                                // Render floor label
                                // if ((item as any).isLabel) {
                                //   return (
                                //     <div
                                //       className={classNames('pms-room-item pms-room-item--floor')}
                                //       key={itemIndex}
                                //     >
                                //       {(item as any).label}
                                //     </div>
                                //   );
                                // }

                                return (
                                  <RoomItem
                                    displayRoom={displayRoom}
                                    id={item.attributes.room_no}
                                    roomId={item.room_id}
                                    bookingLineId={item.booking_line_id}
                                    key={itemIndex}
                                    type={item.room_type_name}
                                    status={item.room_status}
                                    is_dirty={!item.is_clean}
                                    is_group={item.is_group}
                                    room_lock={item?.room_lock}
                                    bookingId={item?.booking_line?.booking_id}
                                    onViewDetail={handleViewDetail}
                                    info={
                                      item.partner_name
                                        ? {
                                            customerName: item.partner_name,
                                            fit: item?.booking_line?.medium_name || '',
                                            rangeDate: `${toLocalTime(
                                              item.check_in,
                                              'DD/MM HH:mm'
                                            )} - ${toLocalTime(item.check_out, 'DD/MM HH:mm')}`,
                                            price: item.subtotal_price,
                                            checkInTime: item.check_in,
                                            checkOutTime: item.check_out,
                                            note: item.note,
                                            adult: item?.booking_line?.adult,
                                            child: item?.booking_line?.child
                                          }
                                        : undefined
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              );
            })}
          </Row>
        );
      }

      return arrayKeys.map((key, index) => {
        const splitBySection = groupDataBySection(groupedData[key]);

        return (
          <Row gutter={[20, 24]} className="w-full" key={index}>
            {fullArrayKeysBySection.map((keySplit: any, indexSplit: number) => {
              const section = splitBySection[keySplit];
              return (
                <Col span={24} lg={12} key={indexSplit}>
                  <div
                    className={classNames('pms-room-map__rooms-section', {
                      'pms-room-map__rooms-section--detail':
                        displayRoom === DISPLAY_ROOM_VALUE.DETAIL
                    })}
                    key={index}
                  >
                    {!!groupedData?.[key]?.length && !!section?.length && (
                      <h4 className="title">
                        {filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR
                          ? t('bookingPage.roomMap.floor')
                          : ''}{' '}
                        {filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.BOOKING
                          ? t(MAP_FILTER_STATUS_LABEL[key])
                          : filterDisplay === FILTER_DISPLAY_ROOM_MAP_VALUE.FLOOR
                            ? getLabelByFloor(key)
                            : key}{' '}
                        ({section?.length || 0})
                      </h4>
                    )}
                    <div className={classNames('pms-room-map__rooms-section__wrapper')}>
                      {section?.map((item: GroupedRoomStatus, itemIndex: number) => (
                        <RoomItem
                          displayRoom={displayRoom}
                          id={item.attributes.room_no}
                          roomId={item.room_id}
                          bookingLineId={item.booking_line_id}
                          key={itemIndex}
                          type={item.room_type_name}
                          status={item.room_status}
                          is_dirty={!item.is_clean}
                          is_group={item.is_group}
                          room_lock={item?.room_lock}
                          bookingId={item?.booking_line?.booking_id}
                          onViewDetail={handleViewDetail}
                          info={
                            item.partner_name
                              ? {
                                  customerName: item.partner_name,
                                  fit: item?.booking_line?.medium_name || '',
                                  rangeDate: `${toLocalTime(
                                    item.check_in,
                                    'DD/MM HH:mm'
                                  )} - ${toLocalTime(item.check_out, 'DD/MM HH:mm')}`,
                                  price: item.subtotal_price,
                                  checkInTime: item.check_in,
                                  checkOutTime: item.check_out,
                                  note: item.note,
                                  adult: item?.booking_line?.adult,
                                  child: item?.booking_line?.child
                                }
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        );
      });
    }

    const splitBySection = groupDataBySection();
    const splitBySectionKeys = Object.keys(splitBySection);
    splitBySectionKeys.sort((a, b) => a.localeCompare(b));

    return (
      <Row gutter={[20, 24]} className="w-full">
        {splitBySectionKeys.map((key: string, index: number) => {
          const section = splitBySection[key];

          return (
            <Col
              span={24}
              lg={12}
              key={index}
              className="pms-room-map__rooms-section__rooms-layout"
            >
              {section?.map((item: GroupedRoomStatus, itemIndex: number) => (
                <RoomItem
                  key={itemIndex}
                  id={item.attributes.room_no}
                  roomId={item.room_id}
                  bookingLineId={item.booking_line_id}
                  type={item.room_type_name}
                  status={item.room_status}
                  is_dirty={!item.is_clean}
                  is_group={item.is_group}
                  displayRoom={displayRoom}
                  room_lock={item?.room_lock}
                  bookingId={item?.booking_line?.booking_id}
                  onViewDetail={handleViewDetail}
                  info={
                    item.partner_name
                      ? {
                          customerName: item.partner_name,
                          fit: item?.booking_line?.medium_name || '',
                          rangeDate: `${toLocalTime(
                            item.check_in,
                            'DD/MM HH:mm'
                          )} - ${toLocalTime(item.check_out, 'DD/MM HH:mm')}`,
                          price: item.subtotal_price,
                          checkInTime: item.check_in,
                          checkOutTime: item.check_out,
                          note: item.note,
                          adult: item?.booking_line?.adult,
                          child: item?.booking_line?.child
                        }
                      : undefined
                  }
                />
              ))}
            </Col>
          );
        })}
      </Row>
    );
  };

  const groupedDataSection = groupDataBySection();
  const arrayKeys = Object.keys(groupedDataSection);
  arrayKeys.sort((a, b) => a.localeCompare(b));

  return (
    <div className="w-full">
      <div className="pms-room-map__rooms-section-split">
        <Row gutter={[20, 24]} className="w-full">
          {arrayKeys.map((key, index) => (
            <Col span={24} lg={12} key={index}>
              <div className="pms-room-map__rooms-section-split__column">
                <h2>{key}</h2>
              </div>
            </Col>
          ))}
        </Row>
      </div>
      <div className="flex w-full flex-col gap-6">{renderByFilterDisplay()}</div>
    </div>
  );
}

export default RoomMapSection;
